import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getManualLedger, updateManualLedger } from '../firebase/database';
import { ManualLedger, ManualLedgerEntry } from '../types';
import toast, { Toaster } from 'react-hot-toast';

const EditManualLedger: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { currentUser } = useAuth();

  // Refs for input fields
  const partyNameRef = useRef<HTMLInputElement>(null);
  const entryDateRefs = useRef<(HTMLInputElement | null)[]>([]);
  const entryParticularsRefs = useRef<(HTMLInputElement | null)[]>([]);
  const entryDebitRefs = useRef<(HTMLInputElement | null)[]>([]);
  const entryCreditRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [ledger, setLedger] = useState<ManualLedger | null>(null);
  const [partyName, setPartyName] = useState('');
  const [openingBalance, setOpeningBalance] = useState<number>(0);
  const [logisticsCompany, setLogisticsCompany] = useState<'VECTOR LOGISTICS' | 'BARQ CARGO SYSTEM PVT LTD.' | ''>('');
  const [entries, setEntries] = useState<ManualLedgerEntry[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showValidation, setShowValidation] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  // Load ledger data
  useEffect(() => {
    const loadLedger = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        const ledgerData = await getManualLedger(id);
        if (!ledgerData) {
          toast.error('Manual ledger not found');
          navigate('/manual-ledgers');
          return;
        }
        setLedger(ledgerData);
        setPartyName(ledgerData.partyName);
        setOpeningBalance(ledgerData.openingBalance || 0);
        setEntries(ledgerData.entries);
        setLogisticsCompany((ledgerData as any).logisticsCompany || '');
      } catch (error) {
        console.error('Error loading ledger:', error);
        toast.error('Error loading ledger data');
        navigate('/manual-ledgers');
      } finally {
        setIsLoading(false);
      }
    };

    loadLedger();
  }, [id, navigate]);

  // Add new entry
  const addEntry = () => {
    const newEntry: ManualLedgerEntry = {
      id: Date.now().toString(),
      date: new Date(),
      particulars: '',
      debit: 0,
      credit: 0,
      balance: 0
    };
    setEntries([...entries, newEntry]);
  };

  // Remove entry
  const removeEntry = (id: string) => {
    if (entries.length <= 1) return; // Keep at least one entry
    setEntries(entries.filter(entry => entry.id !== id));
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear dragOverIndex if we're leaving the entire container
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      setIsDragging(false);
      return;
    }

    const newEntries = [...entries];
    const draggedEntry = newEntries[draggedIndex];
    
    // Remove the dragged entry from its original position
    newEntries.splice(draggedIndex, 1);
    
    // Insert it at the new position
    newEntries.splice(dropIndex, 0, draggedEntry);
    
    // Recalculate balances for all entries
    let runningBalance = openingBalance;
    const updatedEntries = newEntries.map(entry => {
      runningBalance += entry.debit - entry.credit;
      return { ...entry, balance: runningBalance };
    });
    
    setEntries(updatedEntries);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setIsDragging(false);
    
    // Show success animation
    setShowSuccessAnimation(true);
    setTimeout(() => setShowSuccessAnimation(false), 2000);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    setIsDragging(false);
  };

  // Update entry
  const updateEntry = (id: string, field: keyof ManualLedgerEntry, value: any) => {
    setEntries(entries.map((entry, index) => {
      if (entry.id === id) {
        const updatedEntry = { ...entry, [field]: value };
        
        // Calculate balance based on debit and credit
        if (field === 'debit' || field === 'credit') {
          const debit = field === 'debit' ? value : entry.debit;
          const credit = field === 'credit' ? value : entry.credit;
          
          // Calculate running balance including opening balance
          let runningBalance = openingBalance;
          for (let i = 0; i < entries.length; i++) {
            if (i < index) {
              runningBalance += entries[i].debit - entries[i].credit;
            } else if (i === index) {
              runningBalance += debit - credit;
            }
          }
          updatedEntry.balance = runningBalance;
        }
        
        return updatedEntry;
      }
      return entry;
    }));
  };

  // Scroll to first empty field
  const scrollToFirstEmptyField = (errors: Record<string, string>) => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return;

    // Priority order for scrolling to fields
    const fieldPriority = ['partyName'];

    // Find the first error field in priority order
    let targetField = null;
    for (const priorityField of fieldPriority) {
      if (errors[priorityField]) {
        targetField = priorityField;
        break;
      }
    }

    // If no priority field found, check for entry errors
    if (!targetField) {
      for (const errorKey of errorKeys) {
        if (errorKey.startsWith('entry_')) {
          targetField = errorKey;
          break;
        }
      }
    }

    // Scroll to the target field
    if (targetField) {
      let targetRef: React.RefObject<HTMLElement> | null = null;
      
      switch (targetField) {
        case 'partyName':
          targetRef = partyNameRef;
          break;

        default:
          // Handle entry errors
          if (targetField.startsWith('entry_')) {
            const parts = targetField.split('_');
            const index = parseInt(parts[1]);
            const field = parts[2];
            
            if (field === 'date' && entryDateRefs.current[index]) {
              targetRef = { current: entryDateRefs.current[index] };
            } else if (field === 'particulars' && entryParticularsRefs.current[index]) {
              targetRef = { current: entryParticularsRefs.current[index] };
            } else if (field === 'debit' && entryDebitRefs.current[index]) {
              targetRef = { current: entryDebitRefs.current[index] };
            } else if (field === 'credit' && entryCreditRefs.current[index]) {
              targetRef = { current: entryCreditRefs.current[index] };
            }
          }
          break;
      }

      if (targetRef?.current) {
        targetRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        targetRef.current.focus();
      }
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!partyName.trim()) {
      errors['partyName'] = 'Party name is required';
      toast.error('Party name is required', { position: 'bottom-right' });
    }
    if (!logisticsCompany) {
      errors['logisticsCompany'] = 'Logistics company is required';
      toast.error('Logistics company is required', { position: 'bottom-right' });
    }

    
    entries.forEach((entry, index) => {
      if (!entry.date) {
        errors[`entry_${index}_date`] = 'Date is required';
        toast.error(`Entry ${index + 1} date is required`, { position: 'bottom-right' });
      }
      if (!entry.particulars.trim()) {
        errors[`entry_${index}_particulars`] = 'Particulars is required';
        toast.error(`Entry ${index + 1} particulars is required`, { position: 'bottom-right' });
      }
      if (entry.debit < 0) {
        errors[`entry_${index}_debit`] = 'Debit cannot be negative';
        toast.error(`Entry ${index + 1} debit cannot be negative`, { position: 'bottom-right' });
      }
      if (entry.credit < 0) {
        errors[`entry_${index}_credit`] = 'Credit cannot be negative';
        toast.error(`Entry ${index + 1} credit cannot be negative`, { position: 'bottom-right' });
      }
    });
    
    setFieldErrors(errors);
    
    // Scroll to first empty field if validation fails
    if (Object.keys(errors).length > 0) {
      setTimeout(() => scrollToFirstEmptyField(errors), 100);
    }
    
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!currentUser || !id) return;
    
    setShowValidation(true);
    if (!validateForm()) {
      return;
    }
    setError('');
    
    setIsSaving(true);
    try {
      const ledgerData = {
        partyName: partyName.trim(),
        companyName: partyName.trim(), // Use party name as company name
        logisticsCompany: (logisticsCompany as 'VECTOR LOGISTICS' | 'BARQ CARGO SYSTEM PVT LTD.'),
        openingBalance,
        entries: entries.map(entry => ({
          ...entry,
          particulars: entry.particulars.trim()
        })),
        createdBy: currentUser.id
      };

      await updateManualLedger(id, ledgerData);
      toast.success('Ledger updated successfully!', { position: 'bottom-right' });
      navigate('/manual-ledgers');
    } catch (error) {
      console.error('Error updating ledger:', error);
      toast.error('Error updating ledger. Please try again.', { position: 'bottom-right' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading ledger...</p>
        </div>
      </div>
    );
  }

  if (!ledger) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Ledger not found</p>
          <button 
            onClick={() => navigate('/manual-ledgers')}
            className="mt-4 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            Back to Ledgers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 rounded-lg">
      <Toaster />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Edit Manual Ledger</h1>
          <button 
            onClick={() => navigate('/manual-ledgers')} 
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Back to Ledgers
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          {/* Party Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Party Name *
              </label>
              <input
                ref={partyNameRef}
                type="text"
                value={partyName}
                onChange={(e) => setPartyName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter party name"
              />
              {showValidation && fieldErrors['partyName'] && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors['partyName']}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">This will be used as both party name and company name</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Opening Balance
              </label>
              <input
                type="number"
                step="0.01"
                value={openingBalance}
                onChange={(e) => setOpeningBalance(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="0.00"
              />
              <p className="mt-1 text-xs text-gray-500">Initial balance for this ledger</p>
            </div>
          </div>

          {/* Logistics Company */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logistics Company *
            </label>
            <select
              value={logisticsCompany}
              onChange={(e) => setLogisticsCompany(e.target.value as 'VECTOR LOGISTICS' | 'BARQ CARGO SYSTEM PVT LTD.' | '')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select a company</option>
              <option value="VECTOR LOGISTICS">VECTOR LOGISTICS</option>
              <option value="BARQ CARGO SYSTEM PVT LTD.">BARQ CARGO SYSTEM PVT LTD.</option>
            </select>
            {showValidation && fieldErrors['logisticsCompany'] && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors['logisticsCompany']}</p>
            )}
          </div>

          {/* Ledger Entries */}
          <div>
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Ledger Entries</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    💡 You can drag and drop entries to reorder them. The balance will be recalculated automatically.
                  </p>
                </div>
                {showSuccessAnimation && (
                  <div className="flex items-center space-x-2 text-green-600 animate-bounce">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm font-medium">Reordered!</span>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              {entries.map((entry, index) => {
                const isDragged = draggedIndex === index;
                const isDropTarget = dragOverIndex === index && draggedIndex !== index;
                const isShifted = draggedIndex !== null && !isDragged && (
                  (draggedIndex < index && dragOverIndex !== null && dragOverIndex >= index) ||
                  (draggedIndex > index && dragOverIndex !== null && dragOverIndex <= index)
                );

                return (
                  <div key={entry.id} className="relative">
                    {/* Drop zone indicator */}
                    {isDropTarget && (
                      <div className="absolute -top-2 left-0 right-0 h-1 bg-indigo-500 rounded-full shadow-lg z-10 animate-pulse">
                        <div className="absolute -left-1 -top-1 w-3 h-3 bg-indigo-500 rounded-full"></div>
                        <div className="absolute -right-1 -top-1 w-3 h-3 bg-indigo-500 rounded-full"></div>
                      </div>
                    )}
                    
                    <div 
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`
                        border rounded-lg p-4 cursor-move transition-all duration-300 ease-in-out transform
                        ${isDragged 
                          ? 'opacity-40 scale-95 shadow-2xl border-indigo-400 bg-indigo-50 rotate-1' 
                          : isDropTarget
                          ? 'border-indigo-400 bg-indigo-50 shadow-lg scale-105'
                          : isShifted
                          ? 'translate-y-2 border-gray-300'
                          : 'border-gray-200 hover:shadow-md hover:border-indigo-300 hover:scale-[1.02]'
                        }
                        ${isDragging && !isDragged ? 'transition-transform duration-200' : ''}
                      `}
                      style={{
                        transform: isDragged 
                          ? 'scale(0.95) rotate(2deg)' 
                          : isDropTarget 
                          ? 'scale(1.05)' 
                          : isShifted 
                          ? 'translateY(8px)' 
                          : 'scale(1)',
                        zIndex: isDragged ? 1000 : isDropTarget ? 100 : 1
                      }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className={`flex flex-col space-y-1 p-1 rounded transition-all duration-200 ${
                            isDragged ? 'bg-indigo-200' : 'hover:bg-gray-100'
                          }`}>
                            <div className={`w-1.5 h-1.5 rounded-full transition-colors duration-200 ${
                              isDragged ? 'bg-indigo-600' : 'bg-gray-400'
                            }`}></div>
                            <div className={`w-1.5 h-1.5 rounded-full transition-colors duration-200 ${
                              isDragged ? 'bg-indigo-600' : 'bg-gray-400'
                            }`}></div>
                            <div className={`w-1.5 h-1.5 rounded-full transition-colors duration-200 ${
                              isDragged ? 'bg-indigo-600' : 'bg-gray-400'
                            }`}></div>
                          </div>
                          <h4 className={`text-sm font-medium transition-colors duration-200 ${
                            isDragged ? 'text-indigo-700' : 'text-gray-700'
                          }`}>
                            Entry {index + 1}
                          </h4>
                          {isDragging && (
                            <span className="text-xs text-indigo-600 font-medium animate-pulse">
                              Dragging...
                            </span>
                          )}
                        </div>
                    {entries.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEntry(entry.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Date *</label>
                      <input
                        ref={el => entryDateRefs.current[index] = el}
                        type="date"
                        value={entry.date.toISOString().substring(0, 10)}
                        onChange={(e) => updateEntry(entry.id, 'date', new Date(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      />
                      {showValidation && fieldErrors[`entry_${index}_date`] && (
                        <p className="mt-1 text-xs text-red-600">{fieldErrors[`entry_${index}_date`]}</p>
                      )}
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Particulars *</label>
                      <input
                        ref={el => entryParticularsRefs.current[index] = el}
                        type="text"
                        value={entry.particulars}
                        onChange={(e) => updateEntry(entry.id, 'particulars', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        placeholder="Enter particulars"
                      />
                      {showValidation && fieldErrors[`entry_${index}_particulars`] && (
                        <p className="mt-1 text-xs text-red-600">{fieldErrors[`entry_${index}_particulars`]}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Debit</label>
                      <input
                        ref={el => entryDebitRefs.current[index] = el}
                        type="number"
                        step="0.01"
                        min="0"
                        value={entry.debit}
                        onChange={(e) => updateEntry(entry.id, 'debit', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        placeholder="0.00"
                      />
                      {showValidation && fieldErrors[`entry_${index}_debit`] && (
                        <p className="mt-1 text-xs text-red-600">{fieldErrors[`entry_${index}_debit`]}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Credit</label>
                      <input
                        ref={el => entryCreditRefs.current[index] = el}
                        type="number"
                        step="0.01"
                        min="0"
                        value={entry.credit}
                        onChange={(e) => updateEntry(entry.id, 'credit', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        placeholder="0.00"
                      />
                      {showValidation && fieldErrors[`entry_${index}_credit`] && (
                        <p className="mt-1 text-xs text-red-600">{fieldErrors[`entry_${index}_credit`]}</p>
                      )}
                    </div>
                  </div>
                  
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">Balance:</span>
                          <span className={`text-sm font-bold ${entry.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}> 
                            {entry.balance >= 0 ? '+' : ''}{entry.balance.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Add Entry Button - bottom right */}
            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={addEntry}
                className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors duration-200"
              >
                + Add Entry
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              onClick={() => navigate('/manual-ledgers')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Update Ledger'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditManualLedger;
